import type { TreeEntry } from "@/components/file-tree/useFileTree";
import { isSameOrDescendantPath } from "@/lib/environment-files";

/** 直下に続きがあるディレクトリの「さらに表示」行。`depth` は続きを表示する子行の深さ。 */
export interface TreeLoadMoreRow {
	dirPath: string;
	depth: number;
}

export interface VisibleTreeRow {
	entry: TreeEntry;
	depth: number;
	expanded: boolean;
	/**
	 * この行の直後に描画する「さらに表示」行。ディレクトリの最後の子孫行に付き、
	 * 入れ子のディレクトリが同時に続きを持つ場合は内側から順に並ぶ。
	 */
	loadMore?: readonly TreeLoadMoreRow[];
}

/** キー操作と範囲選択に要る行の形。環境のファイルツリー以外の木 (共有ボリュームの段) でも使う。 */
export interface NavigableTreeRow {
	entry: { type: "file" | "directory"; path: string; children?: readonly unknown[] | null };
	depth: number;
	expanded: boolean;
}

export type TreeKeyAction =
	| { type: "move"; path: string; extend: boolean }
	| { type: "setExpanded"; path: string; expanded: boolean }
	| { type: "selectAll" }
	| { type: "rename"; path: string }
	| { type: "delete"; path: string }
	| { type: "clearSelection" }
	/** 何も実行しないが、ブラウザの既定動作だけは止める。 */
	| { type: "suppress" };

/** ディレクトリが展開済みか。遅延読み込みのため、既定はすべて畳んだ状態とする。 */
export function isTreeEntryExpanded(
	path: string,
	overrides: ReadonlyMap<string, boolean>,
): boolean {
	return overrides.get(path) ?? false;
}

/** 読み込み済みのツリー全体を展開状態に依存せず、親から子の順で平坦化する。 */
export function flattenEntries(entries: readonly TreeEntry[]): TreeEntry[] {
	return entries.flatMap((entry) =>
		entry.type === "directory" && entry.children !== null
			? [entry, ...flattenEntries(entry.children)]
			: [entry],
	);
}

/**
 * 展開状態を反映し、描画順の可視行だけを平坦化する。並び順はサーバーが返した順のまま。
 * 直下が未取得のディレクトリは展開できない (取得後に展開行が現れる)。
 */
export function flattenVisibleRows(
	entries: readonly TreeEntry[],
	overrides: ReadonlyMap<string, boolean>,
	rootHasMore = false,
): VisibleTreeRow[] {
	const flatten = (
		siblings: readonly TreeEntry[],
		depth: number,
		dirPath: string,
		hasMore: boolean,
	): VisibleTreeRow[] => {
		const rows = siblings.flatMap((entry): VisibleTreeRow[] => {
			const children = entry.type === "directory" ? entry.children : null;
			const expanded = children !== null && isTreeEntryExpanded(entry.path, overrides);
			const row: VisibleTreeRow = { entry, depth, expanded };
			if (!expanded || children === null) return [row];
			return [
				row,
				...flatten(children, depth + 1, entry.path, entry.type === "directory" && entry.hasMore),
			];
		});
		if (!hasMore || rows.length === 0) return rows;
		const last = rows.at(-1)!;
		rows[rows.length - 1] = {
			...last,
			loadMore: [...(last.loadMore ?? []), { dirPath, depth }],
		};
		return rows;
	};
	return flatten(entries, 0, "", rootHasMore);
}

/** 指定パスとその祖先を展開する。既に展開済みなら参照を維持する。 */
export function withExpandedAncestors(
	overrides: ReadonlyMap<string, boolean>,
	path: string,
): ReadonlyMap<string, boolean> {
	const paths = path.split("/").reduce<string[]>((ancestors, segment) => {
		const parent = ancestors.at(-1);
		ancestors.push(parent ? `${parent}/${segment}` : segment);
		return ancestors;
	}, []);
	if (paths.every((ancestor) => overrides.get(ancestor) === true)) return overrides;

	const next = new Map(overrides);
	for (const ancestor of paths) next.set(ancestor, true);
	return next;
}

/** 現在の選択に対応する可視行を返し、不可視なら最も近い可視祖先へ戻す。 */
export function resolveCurrentRowPath(
	rows: readonly VisibleTreeRow[],
	selectedPath: string | null,
): string | null {
	if (rows.length === 0) return null;
	if (selectedPath) {
		const visible = rows.find((row) => row.entry.path === selectedPath);
		if (visible) return visible.entry.path;
		// 完全一致は上で返しているため、ここで一致する行は必ず真の祖先。最も深い祖先を選ぶ。
		const ancestor = rows
			.filter((row) => isSameOrDescendantPath(selectedPath, row.entry.path))
			.sort((a, b) => b.entry.path.length - a.entry.path.length)[0];
		if (ancestor) return ancestor.entry.path;
	}
	return rows[0].entry.path;
}

/** 削除対象とその子孫を除いた次の可視行、なければ直前の可視行を返す。 */
export function resolveRowAfterRemoval(
	rows: readonly VisibleTreeRow[],
	removedPaths: readonly string[],
): string | null {
	const survives = (row: VisibleTreeRow) =>
		!removedPaths.some((removed) => isSameOrDescendantPath(row.entry.path, removed));
	const removedIndices = rows.flatMap((row, index) => (survives(row) ? [] : [index]));
	if (removedIndices.length === 0) return null;
	// 複数削除では対象が連続しないこともあるため、最後の対象より後ろから残る行を探す。
	const next = rows.slice(removedIndices.at(-1)! + 1).find(survives);
	if (next) return next.entry.path;
	const previous = rows.slice(0, removedIndices[0]).reverse().find(survives);
	return previous?.entry.path ?? null;
}

/** 可視行上でアンカーと対象の間にあるパスを返す。 */
export function resolveSelectionRange(
	rows: readonly NavigableTreeRow[],
	anchorPath: string,
	targetPath: string,
): string[] {
	const anchorIndex = rows.findIndex((row) => row.entry.path === anchorPath);
	const targetIndex = rows.findIndex((row) => row.entry.path === targetPath);
	if (anchorIndex < 0 || targetIndex < 0) return [];
	return rows
		.slice(Math.min(anchorIndex, targetIndex), Math.max(anchorIndex, targetIndex) + 1)
		.map((row) => row.entry.path);
}

/**
 * 範囲選択の結果と、次に保持すべきアンカーを返す。アンカー未設定のときは操作の起点行で
 * 代替するが、その起点は呼び出し側が保持しなければ次の拡張で失われるため戻り値に含める。
 */
export function resolveExtendedSelection(
	rows: readonly NavigableTreeRow[],
	anchorPath: string | null,
	originPath: string,
	targetPath: string,
): { paths: string[]; anchorPath: string } {
	const anchor = anchorPath ?? originPath;
	return { paths: resolveSelectionRange(rows, anchor, targetPath), anchorPath: anchor };
}

/** ツリー行で処理するキー操作を解決する。 */
export function resolveTreeKeyAction({
	key,
	shiftKey,
	metaKey,
	ctrlKey,
	altKey,
	rows,
	currentPath,
	selectedCount,
}: {
	key: string;
	shiftKey: boolean;
	metaKey: boolean;
	ctrlKey: boolean;
	altKey: boolean;
	rows: readonly NavigableTreeRow[];
	currentPath: string;
	selectedCount: number;
}): TreeKeyAction | null {
	// Enter は button の click を合成するため、名前変更を実行しないどの経路でも既定動作を止める。
	// 止めないと handleClick が走り、ファイルが開いて複数選択が1件に畳まれてしまう。
	const declineEnter: TreeKeyAction | null = key === "Enter" ? { type: "suppress" } : null;
	if (altKey) return declineEnter;
	const hasCommandModifier = metaKey || ctrlKey;
	// 削除は選択中の全件に作用するため件数で絞らない。path は選択が空のときの代替対象。
	if (hasCommandModifier && !shiftKey && (key === "Delete" || key === "Backspace")) {
		return { type: "delete", path: currentPath };
	}
	if (hasCommandModifier && !shiftKey && key.toLowerCase() === "a") {
		return { type: "selectAll" };
	}
	if (hasCommandModifier) return declineEnter;

	const currentIndex = rows.findIndex((row) => row.entry.path === currentPath);
	if (currentIndex < 0) return declineEnter;
	const current = rows[currentIndex];
	const move = (index: number): TreeKeyAction => ({
		type: "move",
		path: rows[Math.max(0, Math.min(rows.length - 1, index))].entry.path,
		extend: shiftKey,
	});

	switch (key) {
		case "ArrowUp":
			return move(currentIndex - 1);
		case "ArrowDown":
			return move(currentIndex + 1);
		case "Home":
			return move(0);
		case "End":
			return move(rows.length - 1);
		case "ArrowRight":
			// 直下が未取得のディレクトリでも展開要求は出す (要求を受けた側が取得する)。
			if (current.entry.type !== "directory") return null;
			if (current.entry.children?.length === 0) return null;
			if (!current.expanded) return { type: "setExpanded", path: currentPath, expanded: true };
			return rows[currentIndex + 1]?.depth === current.depth + 1
				? { type: "move", path: rows[currentIndex + 1].entry.path, extend: false }
				: null;
		case "ArrowLeft": {
			if (current.entry.type === "directory" && current.expanded) {
				return { type: "setExpanded", path: currentPath, expanded: false };
			}
			const parent = rows
				.slice(0, currentIndex)
				.reverse()
				.find((row) => row.depth < current.depth);
			return parent ? { type: "move", path: parent.entry.path, extend: false } : null;
		}
		case "Enter":
			return selectedCount <= 1 ? { type: "rename", path: currentPath } : declineEnter;
		case "Escape":
			return { type: "clearSelection" };
		default:
			return null;
	}
}
